/**
 * みまもり会議参加コンポーネント（ログイン必須）
 * 緊急メールのリンクは user-web へ誘導する。管理Webからは認証済み管理者のみ参加できる。
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Typography, message, Card, Spin, Result } from "antd";
import axios from "axios";
import { getAuthBearerHeaders } from "../../firebase/firebase";
import { AgoraMimamoriMeeting } from "./web_parts/types";

const { Title, Text } = Typography;

const Mimamori_Agora_Meeting: React.FC = () => {
  // URLからパラメータを取得
  const { emergencyId } = useParams<{ emergencyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isTimingDebugMode =
    searchParams.get("debug") === "1" ||
    searchParams.get("timingDebug") === "1" ||
    process.env.REACT_APP_TIMING_DEBUG === "true" ||
    (typeof window !== "undefined" && window.localStorage.getItem("caretablet:timingDebug") === "1");
  
  // ** UID方式での参加者識別 **
  const userUid = Number(`${Math.floor(Math.random() * 9000) + 1000}${(Date.now() % 10000).toString().padStart(4, '0')}`);

  // 状態管理
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [meetingInfo, setMeetingInfo] = useState<AgoraMimamoriMeeting | null>(null);
  const initialNameFromUrl = searchParams.get("name")?.trim() || "";
  const [userName, setUserName] = useState<string>(initialNameFromUrl || "みまもり管理者");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [meetingStarted, setMeetingStarted] = useState<boolean>(false);
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const [preJoinTiming, setPreJoinTiming] = useState<{
    status: "idle" | "running" | "success" | "error";
    totalMs?: number;
    apiMs?: number;
    openWaitMs?: number;
  }>({ status: "idle" });

  useEffect(() => {
    console.log("★★ Mimamori_Agora_Meeting mounted");
    console.log("emergencyId:", emergencyId);
    console.log("userUid:", userUid);
  }, [emergencyId, userUid]);

  // 初期データの読み込み
  useEffect(() => {
    const fetchData = async () => {
      if (!emergencyId) {
        console.error('No emergencyId provided');
        setError("緊急IDが指定されていません");
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching meeting data for emergencyId:', emergencyId);
        const headers = await getAuthBearerHeaders();
        const response = await axios.get("/api/getMimamoriMeetingPublic", {
          params: { emergencyId },
          headers,
        });
        const meeting = (response.data?.meeting || null) as AgoraMimamoriMeeting | null;
        if (!meeting) {
          setError("有効な会議情報が見つかりませんでした");
          setIsValid(false);
          setLoading(false);
          return;
        }
        console.log('Selected latest meeting:', meeting);
        setMeetingInfo(meeting);

        // 有効期限のチェック
        const currentTime = Date.now();
        if (meeting.limitTime < currentTime) {
          console.log('Meeting expired, limitTime:', meeting.limitTime, 'currentTime:', currentTime);
          setError("この緊急会議は有効期限が切れています");
          setIsValid(false);
          setLoading(false);
          return;
        }

        // 会議の状態チェック (flgOpen=1の場合は既に開催中だが、参加可能)
        if (meeting.flgOpen === 1) {
          console.log('Meeting already started, setting flag');
          setMeetingInfo(prev => {
            if (prev) {
              return {
                ...prev,
                alreadyStarted: true
              };
            }
            return prev;
          });
        }

      } catch (error) {
        console.error("データ取得エラー:", error);
        const msg = axios.isAxiosError(error)
          ? (error.response?.data?.error || error.message)
          : "データの読み込み中にエラーが発生しました";
        setError(String(msg));
        setIsValid(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [emergencyId]);

  /**
   * 会議参加処理
   */
  const joinMeeting = async () => {
    if (!meetingInfo || !isValid) {
      message.error("会議情報が無効です");
      return;
    }
    if (isJoining || meetingStarted) {
      return;
    }

    try {
      setIsJoining(true);
      setLoading(true);
      setPreJoinTiming({ status: "running" });
      const preJoinStartMs = performance.now();
      console.log('=== JOIN MEETING DEBUG ===');
      console.log('meetingInfo:', meetingInfo);
      console.log('userUid:', userUid);
      console.log('userName:', userName);
      
      const apiStartMs = performance.now();
      const headers = await getAuthBearerHeaders();
      const joinRes = await axios.post("/api/joinMimamoriMeetingPublic", {
        emergencyId: meetingInfo.emergencyID,
        userName,
        userUid,
      }, {
        headers: { "Content-Type": "application/json", ...headers },
      });
      const apiMs = Math.round(performance.now() - apiStartMs);
      console.log("joinMimamoriMeetingPublic result:", joinRes.data);
      setPreJoinTiming((prev) => ({
        ...prev,
        apiMs,
        totalMs: Math.round(performance.now() - preJoinStartMs),
      }));

      // 6. 会議開始フラグをセット
      setMeetingStarted(true);

      // 7. 会議室を開く（user-web のモバイル最適化会議室へ遷移）
      const openWaitStartMs = performance.now();
      setTimeout(() => {
        const meetingPath = `/mimamori-meeting/${meetingInfo.channelID}?name=${encodeURIComponent(userName)}&uid=${userUid}`;
        const openWaitMs = Math.round(performance.now() - openWaitStartMs);
        const totalMs = Math.round(performance.now() - preJoinStartMs);
        setPreJoinTiming({
          status: "success",
          apiMs,
          openWaitMs,
          totalMs,
        });
        console.log("[pre-join-profiler]", {
          status: "success",
          apiMs,
          openWaitMs,
          totalMs,
          emergencyId: meetingInfo.emergencyID,
          channelID: meetingInfo.channelID,
        });
        console.log('Opening meeting path:', meetingPath);
        navigate(meetingPath);
      }, 1000);
      
    } catch (error) {
      console.error("会議参加エラー:", error);
      setPreJoinTiming((prev) => ({
        ...prev,
        status: "error",
        totalMs: prev.totalMs,
      }));
      console.error("[pre-join-profiler]", {
        status: "error",
        emergencyId: meetingInfo.emergencyID,
        error: String(error),
      });
      message.error("会議参加中にエラーが発生しました");
    } finally {
      setIsJoining(false);
      setLoading(false);
    }
  };

  // ローディング中の表示
  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh", 
        flexDirection: "column" 
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16 }}>みまもり会議情報を読み込み中...</Text>
      </div>
    );
  }

  // エラーまたは無効な場合の表示
  if (error || !isValid) {
    return (
      <Result
        status="warning"
        title="みまもり会議が利用できません"
        subTitle={error || "有効な緊急会議が見つかりません"}
        extra={[
          <Button type="primary" key="home" onClick={() => navigate("/")}>
            ホームに戻る
          </Button>
        ]}
      />
    );
  }

  // 会議開始済みの表示
  if (meetingStarted) {
    return (
      <Result
        status="success"
        title="みまもり会議に接続しました"
        subTitle="新しいウィンドウで会議が開始されます"
        extra={[
          <Button key="home" onClick={() => navigate("/")}>
            ホームに戻る
          </Button>
        ]}
      />
    );
  }

  // メイン画面の表示
  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      height: "100vh",
      backgroundColor: "#f5f5f5",
      padding: "20px"
    }}>
      <Card 
        title={<Title level={3} style={{ textAlign: "center", margin: 0 }}>緊急みまもり接続</Title>}
        style={{ 
          width: "100%", 
          maxWidth: 500,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
        }}
      >
        {meetingInfo && (
          <div style={{ marginBottom: 20 }}>
            {isTimingDebugMode && preJoinTiming.status !== "idle" && (
              <div style={{
                backgroundColor: "#fafafa",
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                fontSize: 12,
                lineHeight: 1.6,
              }}>
                <div><strong>接続前計測:</strong> {preJoinTiming.status === "running" ? "計測中" : preJoinTiming.status === "success" ? "完了" : "エラー"}</div>
                <div>合計: {preJoinTiming.totalMs != null ? `${(preJoinTiming.totalMs / 1000).toFixed(2)}秒` : "-"}</div>
                <div>API待ち（joinMimamoriMeetingPublic）: {preJoinTiming.apiMs != null ? `${(preJoinTiming.apiMs / 1000).toFixed(2)}秒` : "-"}</div>
                <div>会議画面オープン待ち: {preJoinTiming.openWaitMs != null ? `${(preJoinTiming.openWaitMs / 1000).toFixed(2)}秒` : "-"}</div>
              </div>
            )}
            <div style={{ 
              backgroundColor: "#f9f9f9", 
              padding: 16, 
              borderRadius: 8,
              marginBottom: 16
            }}>
              <p><strong>デバイス:</strong> {meetingInfo.deviceName || meetingInfo.serialNumber}</p>
              <p><strong>利用者:</strong> {meetingInfo.userName || "不明"}</p>
              <p><strong>会議名:</strong> {meetingInfo.meetingName || "緊急みまもり会議"}</p>
              <p><strong>開始時間:</strong> {new Date(meetingInfo.starttime).toLocaleString()}</p>
              <p><strong>有効期限:</strong> {new Date(meetingInfo.limitTime).toLocaleString()}</p>
              {/* デバッグ情報（開発環境でのみ表示） */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ marginTop: 10, fontSize: '12px', color: '#666', borderTop: '1px solid #ddd', paddingTop: 8 }}>
                  <p><strong>デバッグ情報:</strong></p>
                  <p>ユーザーUID: {userUid}</p>
                  <p>Emergency ID: {emergencyId}</p>
                  <p>Channel ID: {meetingInfo.channelID}</p>
                </div>
              )}
            </div>

            {meetingInfo.alreadyStarted && (
              <div style={{ 
                backgroundColor: "#fffbe6", 
                padding: 16, 
                borderRadius: 8,
                marginBottom: 16,
                border: "1px solid #ffe58f"
              }}>
                <Text strong style={{ color: "#fa8c16" }}>
                  この緊急会議はすでに開始されています。
                </Text>
                <p>会議に参加して状況を確認することができます。</p>
              </div>
            )}

            <Text style={{ display: "block", marginBottom: 16 }}>
              緊急みまもり要請を受けています。接続するとデバイス側のカメラとマイクが自動的に有効になります。
            </Text>

            <div style={{ marginBottom: 16 }}>
              <Text strong>接続する名前:</Text>
              <Input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="みまもり管理者"
                disabled={isJoining}
                style={{ marginTop: 8 }}
              />
            </div>

            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Button 
                type="primary" 
                size="large" 
                onClick={joinMeeting} 
                loading={loading || isJoining}
                disabled={loading || isJoining || !userName.trim()}
                danger
                style={{ 
                  height: "auto", 
                  padding: "10px 20px",
                  fontSize: 18
                }}
              >
                緊急みまもり会議に接続する
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Mimamori_Agora_Meeting;